import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { saveParticipantRequest, getParticipantDetailRequest } from "../api/actions-api";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosNewRounded from "@mui/icons-material/ArrowBackIosNewRounded";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { Grid, Paper } from "@mui/material";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import FontSize from "@tiptap/extension-font-size";
import type { ParticipantResponse } from "../dto/actions-dto";
import { useNotification } from "../context/NotificationContext";


interface FormValues {
    id: string;
    name: string;
    image_index: number | undefined;
    description: string;
};

const images = [
  "/assets/1.png",
  "/assets/2.png",
  "/assets/3.png",
  "/assets/4.png",
  "/assets/5.png",
  "/assets/6.png",
];

const ParticipantEdit: React.FC = () => {
    const { t } = useTranslation();
    const { id, idaction } = useParams<{ id: string; idaction: string }>();
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const [imageSelected, setImageSelected] = useState<number | undefined>(undefined);

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<FormValues>({
        defaultValues: {
            id: "new",
            name: "",
            image_index: undefined,
            description: ""
        }
    });

    const handleBack = () => {
        navigate(`/actions/${idaction}`);
    };

    useEffect(() => {
        if (id === "new") return;

        const fetchParticipantDetail = async () => {
            try {
                const data = await getParticipantDetailRequest(Number(id));

                if (data.image_index !== undefined)
                    setImageSelected(data.image_index - 1);

                reset({
                    id: data.id?.toString(),
                    name: data.name,
                    image_index: data.image_index,
                    description: data.description
                });
            } catch (err: any) {
                console.error(err);
            }
        };

        fetchParticipantDetail();
    }, [id, reset]);

    const onSubmit = async (data: FormValues) => {
        try {
            const participantDetail: ParticipantResponse = {
                ...data,
                id_action: Number(idaction) ?? 0,
                id: data.id === "new" ? undefined : Number(data.id),
                image_index: imageSelected !== undefined ? imageSelected + 1 : imageSelected
            };

            const response = await saveParticipantRequest(participantDetail);
            console.log("Form Data:", participantDetail);
            if (!response.success) {
                showNotification(response.message || "Failed to save participant.");
                return;
            }

            handleBack();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <IconButton onClick={handleBack}>
                    <ArrowBackIosNewRounded />
                </IconButton>

                <h2 style={{ margin: 0 }}>
                    {t(id === "new" ? "participants.new" : "participants.edit")}
                </h2>
            </Stack>

            <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ maxWidth: 700 }}>
                <Stack spacing={2}>

                    <Controller
                        name="name"
                        control={control}
                        rules={{ required: t("participants.name.required") }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t("participants.name")}
                                fullWidth
                                size="small"
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        )}
                    />

                    {/* Rich Text Editor */}
                    <FormControl fullWidth error={!!errors.description}>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => {
                                const editor = useEditor({
                                    extensions: [
                                        StarterKit,
                                        TextStyle,
                                        FontSize.configure({ types: ["textStyle"] })
                                    ],
                                    content: field.value || "",
                                    onUpdate: ({ editor }) => field.onChange(editor.getHTML())
                                });

                                useEffect(() => {
                                    if (editor && field.value !== editor.getHTML()) {
                                        editor.commands.setContent(field.value || "");
                                    }
                                }, [field.value, editor]);

                                const toggleMark = (mark: string) => {
                                    if (!editor) return;

                                    switch (mark) {
                                        case "bold": editor.chain().focus().toggleBold().run(); break;
                                        case "italic": editor.chain().focus().toggleItalic().run(); break;
                                        case "underline": editor.chain().focus().toggleUnderline().run(); break;
                                        case "strike": editor.chain().focus().toggleStrike().run(); break;
                                        case "bulletList": editor.chain().focus().toggleBulletList().run(); break;
                                        case "orderedList": editor.chain().focus().toggleOrderedList().run(); break;
                                        case "link":
                                            const url = prompt("Enter URL") || "";
                                            if (url)
                                                editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
                                            break;
                                    }
                                };

                                return (
                                    <div>

                                        {editor && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{ mb: 1, flexWrap: "wrap", alignItems: "center" }}
                                            >
                                                {["bold", "italic", "underline", "strike", "bulletList", "orderedList", "link"].map(mark => (
                                                    <Button
                                                        key={mark}
                                                        variant={editor.isActive(mark) ? "contained" : "outlined"}
                                                        size="small"
                                                        onClick={() => toggleMark(mark)}
                                                    >
                                                        {mark === "bold" ? "B" :
                                                            mark === "italic" ? "I" :
                                                                mark === "underline" ? "U" :
                                                                    mark === "strike" ? "S" :
                                                                        mark === "bulletList" ? "• List" :
                                                                            mark === "orderedList" ? "1. List" : "Link"}
                                                    </Button>
                                                ))}
                                            </Stack>
                                        )}

                                        <EditorContent
                                            editor={editor}
                                            style={{
                                                minHeight: 150,
                                                border: "1px solid #ccc",
                                                borderRadius: 4,
                                                padding: 8
                                            }}
                                        />

                                    </div>
                                );
                            }}
                        />

                        {errors.description && (
                            <FormHelperText>{errors.description.message}</FormHelperText>
                        )}
                    </FormControl>

                    <Grid container spacing={2} sx={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))"}}>
                        {images.map((src, index) => (
                            <Grid key={index} sx={{ display: 'flex' }}>
                                <Paper
                                    onClick={() => setImageSelected(index)}
                                    elevation={imageSelected === index ? 6 : 1}
                                    sx={{
                                        border: imageSelected === index ? "3px solid blue" : "1px solid #ccc",
                                        cursor: "pointer",
                                        width: 250,
                                        height: 160,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flex: 1, flexDirection: 'column'
                                    }}
                                >
                                    <img src={src} alt="" style={{ maxWidth: '250px', maxHeight: '150px', objectFit: "cover" }} />
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{ mt: 1 }}
                    >
                        {isSubmitting ? <CircularProgress size={22} color="inherit" /> : t("button.save")}
                    </Button>
                </Stack>
            </Box>
        </>
    );
}

export default ParticipantEdit;
